import React, { useEffect, useState } from 'react';
import zxcvbn from 'zxcvbn';
import './PasswordStrengthIndicator.css';

function PasswordStrengthIndicator({ password, userEmail }) {
  const [strength, setStrength] = useState(null);

  useEffect(() => {
    if (password) {
      const userInputs = userEmail ? [userEmail.split('@')[0]] : [];
      const result = zxcvbn(password, userInputs);
      setStrength(result);
    } else {
      setStrength(null);
    }
  }, [password, userEmail]);

  if (!password || !strength) return null;

  const getStrengthLabel = (score) => {
    const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
    return labels[score] || 'Unknown';
  };

  const getStrengthColor = (score) => {
    const colors = ['#dc3545', '#fd7e14', '#ffc107', '#28a745', '#20c997'];
    return colors[score] || '#6c757d';
  };

  const requirements = [
    { 
      met: password.length >= 12, 
      text: 'At least 12 characters' 
    },
    { 
      met: /[A-Z]/.test(password), 
      text: 'One uppercase letter' 
    },
    { 
      met: /[a-z]/.test(password), 
      text: 'One lowercase letter' 
    },
    { 
      met: /\d/.test(password), 
      text: 'One number' 
    },
    { 
      met: /[@$!%*?&#]/.test(password), 
      text: 'One special character (@$!%*?&#)' 
    }
  ];

  return (
    <div className="password-strength-indicator">
      <div className="strength-meter">
        <div className="strength-meter-label">
          Password Strength: <span style={{ color: getStrengthColor(strength.score) }}>
            {getStrengthLabel(strength.score)}
          </span>
        </div>
        <div className="strength-meter-bar">
          <div 
            className="strength-meter-fill"
            style={{ 
              width: `${(strength.score + 1) * 20}%`,
              backgroundColor: getStrengthColor(strength.score)
            }}
          />
        </div>
      </div>

      {strength.crack_times_display && (
        <div className="crack-time">
          Time to crack: {strength.crack_times_display.offline_slow_hashing_1e4_per_second}
        </div>
      )}

      <div className="requirements-list">
        {requirements.map((req, index) => (
          <div key={index} className={`requirement ${req.met ? 'met' : 'unmet'}`}>
            <span className="requirement-icon">
              {req.met ? '✓' : '✗'}
            </span>
            <span className="requirement-text">{req.text}</span>
          </div>
        ))}
      </div>

      {strength.feedback && strength.feedback.suggestions.length > 0 && (
        <div className="suggestions">
          <strong>Suggestions:</strong>
          <ul>
            {strength.feedback.suggestions.map((suggestion, index) => (
              <li key={index}>{suggestion}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default PasswordStrengthIndicator;